window.dataContainerIndexDbCache = {
    db: null,
    DB_NAME: "dataContainerIndexDbCache",
    DEFAULT_STORE: "datacontainer",

    // Open WITHOUT a version to avoid VersionError when DB already exists at higher version.
    init: function () {
        return new Promise((resolve, reject) => {
            if (this.db) {
                console.debug("[IndexedDB] Already initialized; reusing existing handle.");
                resolve(this.db);
                return;
            }

            console.debug("[IndexedDB] Opening database '" + this.DB_NAME + "' (no version)...");
            const request = window.indexedDB.open(this.DB_NAME);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                console.debug("[IndexedDB] Upgrade needed from", event.oldVersion, "to", event.newVersion);
                try {
                    if (!db.objectStoreNames.contains(this.DEFAULT_STORE)) {
                        db.createObjectStore(this.DEFAULT_STORE);
                        console.debug("[IndexedDB] Created object store '" + this.DEFAULT_STORE + "'.");
                    }
                } catch (e) {
                    console.error("[IndexedDB] Error creating object store:", e);
                    reject(e);
                }
            };

            request.onsuccess = (event) => {
                this.db = event.target.result;
                console.debug("[IndexedDB] Opened successfully:", this.db, "version", this.db.version);

                this.db.onversionchange = () => {
                    console.warn("[IndexedDB] Version change requested, closing old connection.");
                    this.db.close();
                    this.db = null;
                };

                resolve(this.db);
            };

            request.onblocked = (event) => {
                console.warn("[IndexedDB] Open request blocked (close other tabs/windows):", event);
            };

            request.onerror = (event) => {
                const err = event.target?.error;
                console.error("[IndexedDB] Error opening:", err?.name || "", err?.message || err || event);
                reject(err?.message || "Error opening IndexedDB");
            };
        });
    },

    // Ensure an object store (segment) exists; if missing, bump version and create it.
    ensureStore: async function (segmentName) {
        const targetStore = segmentName || this.DEFAULT_STORE;
        let db = await this.init();

        if (db.objectStoreNames.contains(targetStore)) {
            return db;
        }

        // Need to bump version to create a new objectStore (upgrade flow)
        return new Promise((resolve, reject) => {
            const newVersion = db.version + 1;
            console.debug(`[IndexedDB] Store '${targetStore}' missing � upgrading DB to v${newVersion}`);
            db.close();
            this.db = null;

            const req = window.indexedDB.open(this.DB_NAME, newVersion);

            req.onupgradeneeded = (event) => {
                const upDb = event.target.result;
                try {
                    if (!upDb.objectStoreNames.contains(targetStore)) {
                        upDb.createObjectStore(targetStore);
                        console.debug("[IndexedDB] Created object store '" + targetStore + "' during upgrade.");
                    }
                } catch (e) {
                    console.error("[IndexedDB] Error creating object store on upgrade:", e);
                    reject(e);
                }
            };

            req.onsuccess = (event) => {
                this.db = event.target.result;
                this.db.onversionchange = () => {
                    console.warn("[IndexedDB] Version change requested, closing old connection.");
                    this.db.close();
                    this.db = null;
                };
                console.debug("[IndexedDB] Upgrade/open success. New version:", this.db.version);
                resolve(this.db);
            };

            req.onblocked = (event) => {
                console.warn("[IndexedDB] Upgrade blocked (close other tabs/windows):", event);
            };

            req.onerror = (event) => {
                const err = event.target?.error;
                console.error("[IndexedDB] Upgrade/open error:", err?.name || "", err?.message || err || event);
                reject(err?.message || "Error upgrading IndexedDB for new segment");
            };
        });
    },

    // Use transaction completion for reliability (commit after oncomplete).
    setValue: async function (key, dataObject, segmentName) {
        const db = await this.ensureStore(segmentName);
        const storeName = segmentName || this.DEFAULT_STORE;

        return new Promise((resolve, reject) => {
            const tx = db.transaction([storeName], "readwrite");
            const store = tx.objectStore(storeName);
            let req;
            try {
                req = store.put(dataObject, key);
            } catch (e) {
                reject(e);
                return;
            }

            tx.oncomplete = () => resolve(true);
            tx.onabort = (e) => reject(tx.error || e?.target?.error || "Transaction aborted saving to '" + storeName + "'");
            tx.onerror = (e) => reject(tx.error || e?.target?.error || "Transaction error saving to '" + storeName + "'");

            req.onerror = (e) => {
                // keep extra detail from request error too
                const err = e?.target?.error;
                console.error("[IndexedDB] put error:", err?.name || "", err?.message || err || e);
            };
        });
    },

    getValue: async function (key, segmentName) {
        const db = await this.ensureStore(segmentName);
        const storeName = segmentName || this.DEFAULT_STORE;

        return new Promise((resolve, reject) => {
            const tx = db.transaction([storeName], "readonly");
            const store = tx.objectStore(storeName);
            let req;
            try {
                req = store.get(key);
            } catch (e) {
                reject(e);
                return;
            }

            tx.oncomplete = () => resolve(req.result ?? null);
            tx.onabort = (e) => reject(tx.error || e?.target?.error || "Transaction aborted reading from '" + storeName + "'");
            tx.onerror = (e) => reject(tx.error || e?.target?.error || "Transaction error reading from '" + storeName + "'");

            req.onerror = (e) => {
                const err = e?.target?.error;
                console.error("[IndexedDB] get error:", err?.name || "", err?.message || err || e);
            };
        });
    },

    // Returns only metadata for a stored entry: { timestamp, length } where length is
    // the decoded byte length of the base64 'data' payload. Avoids transferring the
    // payload across the JS interop boundary (used by cache refresh timestamp checks).
    getValueInfo: async function (key, segmentName) {
        const db = await this.ensureStore(segmentName);
        const storeName = segmentName || this.DEFAULT_STORE;

        return new Promise((resolve, reject) => {
            const tx = db.transaction([storeName], "readonly");
            const store = tx.objectStore(storeName);
            let req;
            try {
                req = store.get(key);
            } catch (e) {
                reject(e);
                return;
            }

            tx.oncomplete = () => {
                const entry = req.result;
                if (!entry || !entry.data) {
                    resolve(null);
                    return;
                }

                let length = Math.floor(entry.data.length / 4) * 3;
                if (entry.data.endsWith("==")) length -= 2;
                else if (entry.data.endsWith("=")) length -= 1;
                if (length < 0) length = 0;

                resolve({ timestamp: entry.timestamp ?? null, length: length });
            };
            tx.onabort = (e) => reject(tx.error || e?.target?.error || "Transaction aborted reading info from '" + storeName + "'");
            tx.onerror = (e) => reject(tx.error || e?.target?.error || "Transaction error reading info from '" + storeName + "'");

            req.onerror = (e) => {
                const err = e?.target?.error;
                console.error("[IndexedDB] getInfo error:", err?.name || "", err?.message || err || e);
            };
        });
    },

    deleteValue: async function (key, segmentName) {
        const db = await this.ensureStore(segmentName);
        const storeName = segmentName || this.DEFAULT_STORE;

        return new Promise((resolve, reject) => {
            const tx = db.transaction([storeName], "readwrite");
            const store = tx.objectStore(storeName);
            let req;

            try {
                req = store.delete(key);
            } catch (e) {
                reject(e);
                return;
            }

            tx.oncomplete = () => resolve(true);
            tx.onabort = (e) => reject(tx.error || e?.target?.error || "Transaction aborted deleting from '" + storeName + "'");
            tx.onerror = (e) => reject(tx.error || e?.target?.error || "Transaction error deleting from '" + storeName + "'");

            req.onerror = (e) => {
                const err = e?.target?.error;
                console.error("[IndexedDB] delete error:", err?.name || "", err?.message || err || e);
            };
        });
    },

    clear: async function (segmentName) {
        const db = await this.ensureStore(segmentName);
        const storeName = segmentName || this.DEFAULT_STORE;

        return new Promise((resolve, reject) => {
            const tx = db.transaction([storeName], "readwrite");
            const store = tx.objectStore(storeName);
            let req;
            try {
                req = store.clear();
            } catch (e) {
                reject(e);
                return;
            }

            tx.oncomplete = () => resolve(true);
            tx.onabort = (e) => reject(tx.error || e?.target?.error || "Transaction aborted clearing '" + storeName + "'");
            tx.onerror = (e) => reject(tx.error || e?.target?.error || "Transaction error clearing '" + storeName + "'");

            req.onerror = (e) => {
                const err = e?.target?.error;
                console.error("[IndexedDB] clear error:", err?.name || "", err?.message || err || e);
            };
        });
    }
};
